import { fetchFeed, feedUrl, feedParams, toRows, pick, num, fmtDate, setText } from "./feeds.js";

// Live holdings from the get_holdings feed: top 10 table, holdings count, as-of date,
// and the "Download holdings (CSV)" button backed by get_holdings_file.
//
// The page ships with a hand-maintained table and a static CSV (assets/data) as the
// fallback: this code only overwrites them when the feed actually returns rows, so an
// Ultimus login timeout leaves the last manual snapshot on screen instead of a blank.
//
// Lines are listed individually (no issuer aggregation); cash / currency lines such as
// Receivables/Payables and zero-value lines are left out; weights are % of net assets.
export async function getHoldings(fundID) {
    wireCsvButton(fundID);

    const rows = toRows(await fetchFeed("get_holdings", fundID));
    const lines = rows
        .map((r) => ({
            name: pick(r, ["securityDescriptionLong", "securityDescriptionShort", "securityDescription", "securityName", "name"]),
            short: pick(r, ["securityDescriptionShort", "securityDescription", "name"]),
            ticker: cleanTicker(pick(r, ["securityTicker", "ticker", "symbol"])),
            identifier: pick(r, ["securityIdentifier", "cusip", "isin"]),
            hasIdentifierField: r.securityIdentifier !== undefined || r.cusip !== undefined,
            segment: String(pick(r, ["segment", "category", "assetClass"]) || ""),
            weight: num(pick(r, ["netAssetsPercent", "marketValuePercent", "percentOfNetAssets", "percentNetAssets", "weight", "weighting"])),
            asOf: pick(r, ["asOfDate", "holdingsDate", "date"]),
        }))
        .filter((i) => i.name && i.weight != null && i.weight > 0)
        .filter((i) => !i.hasIdentifierField || (i.identifier != null && i.identifier !== ""))
        .filter((i) => !isCash(i))
        .sort((a, b) => b.weight - a.weight);

    if (!lines.length) return;

    // Ultimus reports weights as fractions (0.0523); some feeds send percentages already.
    const total = lines.reduce((s, i) => s + Math.abs(i.weight), 0);
    const scale = total > 1.5 ? 1 : 100;

    setText(".holdingsdatetable", fmtDate(lines[0].asOf));
    setText("#data_holdingsCount", lines.length);

    const tr = lines
        .slice(0, 10)
        .map((i) => "<tr><td>" + i.name + "</td><td>" + i.ticker + "</td><td class=\"num\">" + (i.weight * scale).toFixed(2) + "%</td></tr>")
        .join("");
    $("#topTenHoldings").html(tr);
}

function isCash(i) {
    if (/currency|cash/i.test(i.segment)) return true;
    const n = (i.name + " " + (i.short || "")).toLowerCase();
    return /receivables|payables|^cash\b|cash (&|and) cash equivalents|cash equivalents|money market/.test(n);
}

// Bloomberg-style tickers arrive as "ALGN US", "CSU CN", "RR/ LN"; drop the US suffix, keep foreign exchange codes.
function cleanTicker(t) {
    return String(t || "").replace(/\s+US$/i, "").trim();
}

// On click, fetch the live file (the feed answers {cached, data, error} with the CSV
// text in `data`, or possibly a raw CSV) and save it. If the feed has nothing, fall
// back to the static CSV the button's href already points at.
function wireCsvButton(fundID) {
    const $btn = $("#csvlink");
    const fallbackHref = $btn.attr("href");
    const fallbackName = $btn.attr("download") || "Sequoia_ETF_Holdings.csv";

    $btn.on("click", function (e) {
        e.preventDefault();
        $.ajax({ type: "post", url: feedUrl("get_holdings_file"), data: feedParams(fundID), dataType: "text" })
            .done((text) => {
                const csv = extractCsv(text);
                if (csv) saveCsv(csv, "Sequoia_ETF_Holdings_" + (csvDate(csv) || todayStamp()) + ".csv");
                else useFallback("returned no CSV");
            })
            .fail((xhr, status) => useFallback("request failed: " + status));
    });

    function useFallback(why) {
        console.warn("[feed] get_holdings_file " + why + "; serving the static CSV instead.");
        if (!fallbackHref) return;
        const a = document.createElement("a");
        a.href = fallbackHref;
        a.download = fallbackName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    }
}

function extractCsv(text) {
    if (!text) return null;
    let parsed = null;
    try { parsed = JSON.parse(text); } catch (e) { return text.trim() ? text : null; }
    if (parsed == null || typeof parsed !== "object") return null;
    if (parsed.error) console.warn("[feed] get_holdings_file reported:", parsed.error);
    const data = "data" in parsed ? parsed.data : parsed;
    if (typeof data === "string") return data.trim() ? data : null;
    const rows = toRows(data);
    if (!rows.length) return null;
    const cols = Object.keys(rows[0]).filter((c) => c !== "cached");
    const esc = (v) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    return [cols.map(esc).join(",")].concat(rows.map((r) => cols.map((c) => esc(r[c])).join(","))).join("\n");
}

// The file's second line reads "Fund Holdings Data as of 10/16/2026"; use that date in the filename.
function csvDate(csv) {
    const m = csv.match(/as of\s+(\d{1,2})\/(\d{1,2})\/(\d{4})/i);
    if (!m) return null;
    const p = (n) => String(n).padStart(2, "0");
    return p(m[1]) + "-" + p(m[2]) + "-" + m[3];
}

function saveCsv(csv, filename) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
        return;
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}

function todayStamp() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return p(d.getMonth() + 1) + "-" + p(d.getDate()) + "-" + d.getFullYear();
}
